/**
 * StoryPage (web) — the full Story article at `/story/:storyId`, inside `AppLayout`. The article itself
 * is `StoryView`, shared with the admin's story preview. Stories load from Firestore, so a deep link
 * shows a loading state until the published list has answered, and only then "not found".
 */
import { useEffect } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { generatePageTitle } from '@elara/client-core/lib/page-title';

import { useRouteParams, useRouterState, useStoriesState } from '#states/hooks.js';
import { AppLayout } from '#views/layout/AppLayout.jsx';
import { StoryView } from '#views/stories/StoryView.js';

export function StoryPage() {
  const routerState = useRouterState();
  const stories = useStoriesState();
  const { storyId } = useRouteParams();
  const story = storyId ? stories.find(storyId) : undefined;

  // The route's static "Story" title is a fallback; set the browser title to the story's own title
  // (mirrors how TripPage overrides its route title with the trip name).
  useEffect(() => {
    routerState.setPageTitle(generatePageTitle(story?.title ?? 'Story'));
  }, [routerState, story?.title]);

  // Once per story opened, not per re-render.
  const openedStory = story?.id;
  useEffect(() => {
    if (story) stories.trackOpened(story);
  }, [openedStory]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!story) {
    return (
      <AppLayout transitionKey={routerState.currentRoute?.path}>
        <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
          {stories.isReady ? 'Story not found.' : 'Loading story…'}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={story.title} transitionKey={`story-${story.id}`} backButtonRoute={ROUTES.HOME}>
      <div className="h-full overflow-y-auto">
        <StoryView story={story} />
      </div>
    </AppLayout>
  );
}
